/**
 * Browser/runtime shims needed before pdf.js runs (Shows/Music PDF import).
 *
 * 1) Promise.try / Promise.withResolvers — missing on older Safari; pdfjs 5.5+ uses them.
 * 2) Uint8Array.prototype.toHex — pdf.worker `fingerprints` calls `.toHex()` on MD5 / ID bytes
 *    that are plain Uint8Arrays (see pdfjs-dist build pdf.worker.mjs ~59575). Without this,
 *    Safari throws `n.toHex is not a function` when parsing some PDFs.
 *
 * @see https://github.com/tc39/proposal-promise-try
 * @see https://github.com/tc39/proposal-promise-with-resolvers
 */

function installPromiseExtensions(): void {
  if (typeof Promise === 'undefined') return

  const P = Promise as typeof Promise & {
    try?: (fn: (...args: unknown[]) => unknown, ...args: unknown[]) => Promise<unknown>
    withResolvers?: <T>() => {
      promise: Promise<T>
      resolve: (value: T | PromiseLike<T>) => void
      reject: (reason?: unknown) => void
    }
  }

  if (typeof P.try !== 'function') {
    P.try = function promiseTry<T>(
      fn: (...args: unknown[]) => T,
      ...args: unknown[]
    ): Promise<Awaited<T>> {
      try {
        return Promise.resolve(fn(...args)) as Promise<Awaited<T>>
      } catch (e) {
        return Promise.reject(e)
      }
    }
  }

  if (typeof P.withResolvers !== 'function') {
    P.withResolvers = function withResolvers<T>() {
      let resolve!: (value: T | PromiseLike<T>) => void
      let reject!: (reason?: unknown) => void
      const promise = new Promise<T>((res, rej) => {
        resolve = res
        reject = rej
      })
      return { promise, resolve, reject }
    }
  }
}

function installUint8ArrayToHex(): void {
  if (typeof Uint8Array === 'undefined') return
  const proto = Uint8Array.prototype as Uint8Array & { toHex?: () => string }
  if (typeof proto.toHex === 'function') return
  Object.defineProperty(proto, 'toHex', {
    configurable: true,
    enumerable: false,
    writable: true,
    value: function toHex(this: Uint8Array): string {
      let s = ''
      for (let i = 0; i < this.length; i++) {
        s += (this[i] ?? 0).toString(16).padStart(2, '0')
      }
      return s
    },
  })
}

installPromiseExtensions()
installUint8ArrayToHex()
